import type { BillingModuleKey } from '@bill-alps/backend/features/companies/modules';
import type { Plan } from '@bill-alps/backend/shared/billing/planLimits';
import {
  ALL_BILLING_MODULES,
  isBillingModuleKey,
  normalizeModuleSet,
  packagePriceFor,
  type BillingInterval,
  type PaidPlan,
} from '@bill-alps/backend/shared/billing/plans';

export type { BillingInterval, BillingModuleKey, PaidPlan, Plan };

/** What the user picked before/at signup or on the account page. */
export interface DesiredBillingPackage {
  plan: Plan;
  modules: BillingModuleKey[];
  interval: BillingInterval;
}

export const DESIRED_BILLING_PACKAGE_STORAGE_KEY =
  'bill-alps:desired-billing-package';

export function isPlan(value: string | null | undefined): value is Plan {
  return value === 'free' || value === 'team' || value === 'business';
}

export function isPaidPlan(
  value: Plan | string | null | undefined,
): value is PaidPlan {
  return value === 'team' || value === 'business';
}

export function isBillingInterval(
  value: string | null | undefined,
): value is BillingInterval {
  return value === 'month' || value === 'year';
}

export function normalizePlan(value: string | null | undefined): Plan {
  return isPlan(value) ? value : 'free';
}

export function normalizeInterval(
  value: string | null | undefined,
): BillingInterval {
  return isBillingInterval(value) ? value : 'month';
}

/**
 * `?modules=invoicing,payroll,accounting`. The legacy `both` (the website's
 * old two-module toggle) still resolves to invoicing + payroll; anything
 * unreadable or empty falls back to the full Suite, which is what the
 * website links to by default.
 */
export function parseModules(
  value: string | readonly string[] | null | undefined,
): BillingModuleKey[] {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const keys = raw
    .map((entry) => entry.trim())
    .flatMap((entry): BillingModuleKey[] =>
      entry === 'both'
        ? ['invoicing', 'payroll']
        : isBillingModuleKey(entry)
          ? [entry]
          : [],
    );
  return keys.length > 0 ? normalizeModuleSet(keys) : [...ALL_BILLING_MODULES];
}

/** Price of a selection for one billing period (month or year), in CHF. */
export function priceFor(
  plan: Plan,
  modules: readonly BillingModuleKey[],
  interval: BillingInterval = 'month',
): number {
  if (!isPaidPlan(plan) || modules.length === 0) return 0;
  return packagePriceFor(plan, modules, interval);
}

// The module set a company's subscription currently pays for is billing state
// now, not something the module flags can be inverted back into: a partial
// package no longer switches the other modules off. Read it with
// `getBilledModules` from `@bill-alps/backend/shared/billing/moduleEntitlements`
// — the same helper the backend gates use — rather than deriving it here.

export function getDesiredBillingPackageFromSearchParams(
  searchParams: URLSearchParams,
): DesiredBillingPackage {
  return {
    plan: normalizePlan(searchParams.get('plan')),
    modules: parseModules(searchParams.get('modules')),
    interval: normalizeInterval(searchParams.get('interval')),
  };
}

export function billingPackageToSearchParams({
  plan,
  modules,
  interval,
}: DesiredBillingPackage) {
  const params = new URLSearchParams();
  params.set('plan', plan);
  params.set('modules', modules.join(','));
  params.set('interval', interval);
  return params;
}

export function billingPackageQueryString(
  desiredPackage: DesiredBillingPackage,
) {
  const query = billingPackageToSearchParams(desiredPackage).toString();
  return query ? `?${query}` : '';
}

export function storeDesiredBillingPackage(
  desiredPackage: DesiredBillingPackage,
) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(
    DESIRED_BILLING_PACKAGE_STORAGE_KEY,
    JSON.stringify(desiredPackage),
  );
}

export function readStoredDesiredBillingPackage(): DesiredBillingPackage | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(
    DESIRED_BILLING_PACKAGE_STORAGE_KEY,
  );
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as {
      plan?: string;
      modules?: string | string[];
      interval?: string;
    };
    return {
      plan: normalizePlan(parsed.plan),
      modules: parseModules(parsed.modules),
      interval: normalizeInterval(parsed.interval),
    };
  } catch {
    return null;
  }
}

export function clearStoredDesiredBillingPackage() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(DESIRED_BILLING_PACKAGE_STORAGE_KEY);
}

export function getDesiredBillingPackage(
  searchParams: URLSearchParams,
): DesiredBillingPackage {
  const fromParams = getDesiredBillingPackageFromSearchParams(searchParams);
  if (
    searchParams.has('plan') ||
    searchParams.has('modules') ||
    searchParams.has('interval')
  ) {
    return fromParams;
  }
  return readStoredDesiredBillingPackage() ?? fromParams;
}
